import { createApi } from '@reduxjs/toolkit/query/react'
import { HcError } from '@/common/types/api.def.ts'
import { downloadFile } from '@/utils/misc.ts'
import {
  GetAllHeadcountReportRequest,
  GetCurrentEmployeeHeadcountReportRequest,
  GetDepartmentHeadcountReportRequest,
  GetHeadcountForMdmReportRequest,
  GetPagStaffListRequest,
  GetPeopleDashboardDataRequest,
  GetPmHeadcountReportRequest,
} from '@/features/headcount/headcount-request.def.ts'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import { Department } from '@/common/types/work-info.def.ts'


export const headcountApi = createApi({
  reducerPath: 'headcountApi',
  baseQuery: hcPortalBaseQuery(),
  endpoints: builder => ({
    downloadAllHeadcountReport: builder.mutation<void, GetAllHeadcountReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetAllHeadcountReport',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`all_headcount_${request.endDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadPmHeadcountReport: builder.mutation<void, GetPmHeadcountReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetPmHeadcountReport',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`pm_headcount_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadHeadcountForMdmReport: builder.mutation<void, GetHeadcountForMdmReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetHeadcountForMdmReport',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`headcount_for_mdm_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadCurrentEmployeeHeadcountReport: builder.mutation<void, GetCurrentEmployeeHeadcountReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetCurrentEmployeeHeadcountReport',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`employee_data_report${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadPeopleDashboardData: builder.mutation<void, GetPeopleDashboardDataRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetPeopleDashboardData',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`people_dashboard_data_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadPagStaffListReport: builder.mutation<void, GetPagStaffListRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetPagStaffListReport',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`pag_staff_list_report_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadFinanceHeadcountReport: builder.mutation<void, GetDepartmentHeadcountReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetDepartmentHeadcountReport',
          method: 'GET',
          cache: 'no-cache',
          params: {
            ...request,
            department: Department.Finance,
          },
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`finance_headcount_report_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadRiskHeadcountReport: builder.mutation<void, GetDepartmentHeadcountReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetDepartmentHeadcountReport',
          method: 'GET',
          cache: 'no-cache',
          params: {
            ...request,
            department: Department.RiskQuantAnalytics,
          },
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`risk_headcount_report_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
    downloadComplianceHeadcountReport: builder.mutation<void, GetDepartmentHeadcountReportRequest>({
      query: (request) => {
        return {
          url: '/Headcount/GetDepartmentHeadcountReport',
          method: 'GET',
          cache: 'no-cache',
          params: {
            ...request,
            department: Department.Compliance,
          },
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(`compliance_headcount_report_${request.targetDate}.xlsx`, blob)
          },
        }
      },
    }),
  }),
})

export const {
  useDownloadAllHeadcountReportMutation,
  useDownloadPmHeadcountReportMutation,
  useDownloadHeadcountForMdmReportMutation,
  useDownloadCurrentEmployeeHeadcountReportMutation,
  useDownloadPeopleDashboardDataMutation,
  useDownloadPagStaffListReportMutation,
  useDownloadFinanceHeadcountReportMutation,
  useDownloadComplianceHeadcountReportMutation,
  useDownloadRiskHeadcountReportMutation,
} = headcountApi